import { RENDER_FULL_USUAL_HOURS } from './render.data';

// How long the paid videos still take, from what the renderer writes on every frame (Yahor,
// 2026-11-04, dev only so far): framesTotal*, currentFrame* and avgFrameSec*, one set per
// orientation. Each is its own GPU job, so the slower one decides.
//
// Measured on dev the same day (per-frame pod logs of three full runs): frames are even, p10–p90
// 16.1–17.1 s, so the average foretells a run without a restart to about 5% from its first minutes.
// What it cannot foretell is a Spot VM restart: six on one of the runs, 5–7 min idle each, one 40 min.
// So nothing here counts down through a silence, and the line stops naming a time when one drags on.
//
// These numbers only ever choose a sentence. Whatever is missing, wrong or out of range means the
// usual time instead: never an error, never a nonsense figure.

// What is left after the last frame: the video put together and uploaded. Measured last frame →
// `finished` on 720 frames: 2.3–3.5 min (Yahor's estimate: about 2).
const AFTER_THE_FRAMES_S = 180;

// Beyond these the numbers are wrong, not slow: seconds that are really milliseconds, say. Real
// frames take 13–25 s, real runs 120–900 frames.
const MIN_FRAME_S = 1;
const MAX_FRAME_S = 1800;
const MAX_FRAMES = 20000;
const MAX_LEFT_S = 48 * 3600;

// The renderer's own percentage while it renders frames: 10 + 90 × frame / total, written in 2%
// steps (live document, 2026-11-04: at most 1.9 behind). Too far from it, frame and total are not
// this run's: they are never reset, so a second render starts with the last one's (Yahor).
const FRAMES_BAR_FROM = 10;
const FRAMES_BAR_SLACK = 6;

// A restart measured 5–7 min (see above): a silence that long is routine, and the line waits it out.
const SILENCE_MS = 10 * 60 * 1000;
const SILENCE_FRAMES = 5;

// A slightly higher estimate is not worth moving the line back for: the count waits for it instead.
const RISE_IGNORED_S = 60;
const RISE_IGNORED_SHARE = 0.1;

// One orientation as the estimate reads it. 'waiting': not at its frames, before they start or going
// through the early statuses again after a restart. 'unknown': at its frames, without numbers to go
// by; `rejected` names a value that was there and made no sense, for the console.
export type RenderEtaSide =
  | { state: 'frames'; frame: number; secondsLeft: number; frameSeconds: number }
  | { state: 'finishing' }
  | { state: 'waiting' }
  | { state: 'unknown'; rejected?: string };

export interface RenderEtaReading {
  sides: ReadonlyArray<RenderEtaSide>;
  // When the run was paid for or asked for, where the document says.
  startedAt?: number;
}

export type RenderEtaView =
  | { kind: 'left'; seconds: number }
  | { kind: 'finishing' }
  | { kind: 'usual' }
  | { kind: 'late' };

// A number from whatever the document holds: a plain number or its string, nothing else. Nothing is
// coerced on the way, which a map could make throw.
export function renderEtaNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function rejected(field: string, value: unknown): RenderEtaSide {
  const shown = typeof value === 'number' || typeof value === 'string' ? value : typeof value;
  return { state: 'unknown', rejected: `${field} = ${shown}` };
}

// One orientation while it renders frames, from its four fields as they stand in the document.
export function renderEtaFrames(framesTotal: unknown, currentFrame: unknown, avgFrameSec: unknown, reportedProgress: unknown): RenderEtaSide {
  // The renderer writes them from its first and second frames on; prod has none of them yet.
  if ([framesTotal, currentFrame, avgFrameSec].some((value) => value === undefined || value === null)) {
    return { state: 'unknown' };
  }
  const total = renderEtaNumber(framesTotal);
  if (total === null || !Number.isInteger(total) || total < 1 || total > MAX_FRAMES) {
    return rejected('framesTotal', framesTotal);
  }
  const frame = renderEtaNumber(currentFrame);
  if (frame === null || !Number.isInteger(frame) || frame < 0 || frame > total) {
    return rejected('currentFrame', currentFrame);
  }
  const progress = renderEtaNumber(reportedProgress);
  if (progress === null || Math.abs(FRAMES_BAR_FROM + ((100 - FRAMES_BAR_FROM) * frame) / total - progress) > FRAMES_BAR_SLACK) {
    return rejected(`loadProgress at frame ${frame} of ${total}`, reportedProgress);
  }
  if (frame === total) {
    return { state: 'finishing' };
  }
  const frameSeconds = renderEtaNumber(avgFrameSec);
  if (frameSeconds === null || frameSeconds < MIN_FRAME_S || frameSeconds > MAX_FRAME_S) {
    return rejected('avgFrameSec', avgFrameSec);
  }
  const secondsLeft = (total - frame) * frameSeconds;
  return secondsLeft > MAX_LEFT_S ? rejected('seconds left', secondsLeft) : { state: 'frames', frame, secondsLeft, frameSeconds };
}

// The estimate as it goes on through a run. Between frames it counts down by itself, but never past
// one frame's time: a machine that went away must not read as a render about to end. Through a restart
// the last estimate stands, until the silence is longer than a restart. Numbers that stop making sense
// take the estimate with them: nothing is shown from what can no longer be read.
export class RenderEtaClock {
  private estimate: { seconds: number; at: number; countsFor: number } | null = null;
  private lastReported: number | null = null;
  private finishing = false;
  private startedAt: number | null = null;
  // Each side's last frame, for as long as it still has frames to render.
  private readonly lastFrames = new Map<number, { frame: number; at: number; patienceMs: number }>();

  public update(reading: RenderEtaReading | undefined, now: number): void {
    const sides = reading?.sides ?? [];
    this.startedAt = reading?.startedAt ?? null;
    this.finishing = !!sides.length && sides.every((side) => side.state === 'finishing');
    sides.forEach((side, index) => {
      if (side.state === 'finishing') {
        this.lastFrames.delete(index);
      } else if (side.state === 'frames' && this.lastFrames.get(index)?.frame !== side.frame) {
        this.lastFrames.set(index, { frame: side.frame, at: now, patienceMs: Math.max(SILENCE_MS, SILENCE_FRAMES * side.frameSeconds * 1000) });
      }
    });
    if (!sides.length || !this.lastFrames.size || sides.some((side) => side.state === 'unknown')) {
      this.estimate = null;
      this.lastReported = null;
    }
    if (!sides.length || this.finishing || sides.some((side) => side.state === 'unknown' || side.state === 'waiting')) {
      return;
    }
    let slowest = { secondsLeft: 0, frameSeconds: 0 };
    for (const side of sides) {
      if (side.state === 'frames' && side.secondsLeft > slowest.secondsLeft) {
        slowest = side;
      }
    }
    const seconds = slowest.secondsLeft + AFTER_THE_FRAMES_S;
    if (seconds === this.lastReported) {
      return;
    }
    this.lastReported = seconds;
    const counted = this.countedAt(now);
    if (counted === null || seconds <= counted || seconds - counted > Math.max(RISE_IGNORED_S, counted * RISE_IGNORED_SHARE)) {
      this.estimate = { seconds, at: now, countsFor: slowest.frameSeconds };
    }
  }

  public view(now: number): RenderEtaView {
    if (this.finishing) {
      return { kind: 'finishing' };
    }
    const silent = [...this.lastFrames.values()].some((side) => now - side.at > side.patienceMs);
    const seconds = silent ? null : this.countedAt(now);
    if (seconds !== null) {
      return { kind: 'left', seconds };
    }
    return this.startedAt !== null && now - this.startedAt > RENDER_FULL_USUAL_HOURS * 3600000 ? { kind: 'late' } : { kind: 'usual' };
  }

  private countedAt(now: number): number | null {
    if (!this.estimate) {
      return null;
    }
    const elapsed = Math.min(Math.max(0, now - this.estimate.at) / 1000, this.estimate.countsFor);
    return this.estimate.seconds - elapsed;
  }
}

// The sentence for a view. Hours of render are not known to the minute: the steps get coarser with
// the time left, always rounding up.
export function renderEtaLine(view: RenderEtaView): string {
  switch (view.kind) {
    case 'left':
      return `${timeLeft(view.seconds)}.`;
    case 'finishing':
      return 'Almost ready.';
    case 'late':
      return 'Taking a little longer than usual.';
    default:
      return `No exact time right now. Renders usually take about ${RENDER_FULL_USUAL_HOURS} hours.`;
  }
}

function timeLeft(seconds: number): string {
  const minutes = seconds / 60;
  if (minutes < 5) {
    return 'A few minutes left';
  }
  if (minutes >= 600) {
    return `About ${Math.ceil(minutes / 60)} hours left`;
  }
  const step = minutes >= 120 ? 15 : minutes >= 60 ? 10 : 5;
  const rounded = Math.ceil(minutes / step) * step;
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (!hours) {
    return `About ${rest} min left`;
  }
  return rest ? `About ${hours} h ${rest} min left` : `About ${hours} hour${hours === 1 ? '' : 's'} left`;
}
